import { push } from 'connected-react-router'
import {
  addDays,
  format,
  isSameDay,
  subDays,
  subMonths,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  parse
} from 'date-fns/esm'
import { FC, useEffect, useState } from 'react'
import { generatePath, useParams } from 'react-router'
import { isFulfilled } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from '../../store'

import {
  getDailyInsightsBySocialNetworkAndMetricThunk,
  getSummaryInsightsBySocialNetworkAndMetricThunk,
  getTopPerformanceThunk
} from '../../store/project/thunks'
import {
  TGetDailyInsightsBySocialNetworkAndMetricPayload,
  TGetSummaryInsightsBySocialNetworkAndMetricPayload
} from '../../store/project/types'

import AnalysticsSocialToggler from '../../components/Analytics/AnalysticsSocialToggler'
import Graphic from '../../components/Analytics/Graphic'
import MainHeader from '../../components/Calendar/Header'
import DateRangePicker from '../../components/UI/Inputs/DateRangePicker'
import Preloader from '../../components/Preloader'

import { SOCIAL_NETWORK } from '../../models/post'
import { AUTH_ROUTE } from '../App/routes'
import {
  AVAILABLE_FACEBOOK_METRICS,
  AVAILABLE_GRAPHIC,
  AVAILABLE_INSTAGRAM_METRICS,
  AVAILABLE_LINKEDIN_METRICS,
  AVAILABLE_TWITTER_METRICS,
  SOCIAL_GRAPHICS,
  TYPE_METRIC,
  TYPE_GRAPHIC,
  TMetric,
  TGraphic,
  totalEngagement,
  TMetricValue,
  INTERVAL,
  TInterval,
  TTopPerformance,
  ITopPerformance
} from '../../models/analytics'
import { preperDataForCharts } from '../../utils/chartHelper'

import EmptyBackgorund from '../../assets/img/empty-approval.png'
import './Analytics.scss'
import { setHeaderView } from '../../store/application/slice'
import { HEADER_VIEW } from '../../store/application/types'

import TopPerformance from '../../components/Analytics/TopPerformance'
import QuestionIcon from '../../components/UI/Icons/Question'

const metricsForRequest = {
  [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: [
    AVAILABLE_FACEBOOK_METRICS.page_impressions_organic_unique,
    AVAILABLE_FACEBOOK_METRICS.page_impressions_paid_unique,
    AVAILABLE_FACEBOOK_METRICS.page_impressions_paid,
    AVAILABLE_FACEBOOK_METRICS.page_impressions_organic,
    AVAILABLE_FACEBOOK_METRICS.page_post_engagements,
    AVAILABLE_FACEBOOK_METRICS.page_impressions,
    AVAILABLE_FACEBOOK_METRICS.page_fan_adds,
    AVAILABLE_FACEBOOK_METRICS.page_fan_removes,
    AVAILABLE_FACEBOOK_METRICS.page_fans
  ],
  [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: [
    AVAILABLE_INSTAGRAM_METRICS.follower_count,
    AVAILABLE_INSTAGRAM_METRICS.impressions,
    AVAILABLE_INSTAGRAM_METRICS.reach,
    AVAILABLE_INSTAGRAM_METRICS.profile_views,
    AVAILABLE_INSTAGRAM_METRICS.story_reach,
    AVAILABLE_INSTAGRAM_METRICS.story_engagement,
    AVAILABLE_INSTAGRAM_METRICS.page_fan_adds
  ],
  [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: [
    AVAILABLE_LINKEDIN_METRICS.engagementRate,
    AVAILABLE_LINKEDIN_METRICS.impression_count,
    AVAILABLE_LINKEDIN_METRICS.share_count,
    AVAILABLE_LINKEDIN_METRICS.click_count,
    AVAILABLE_LINKEDIN_METRICS.like_count,
    AVAILABLE_LINKEDIN_METRICS.comment_count,
    AVAILABLE_LINKEDIN_METRICS.paid_follower_count,
    AVAILABLE_LINKEDIN_METRICS.organic_follower_count,
    AVAILABLE_LINKEDIN_METRICS.total_page_views,
    AVAILABLE_LINKEDIN_METRICS.organic_follower_count_add,
    AVAILABLE_LINKEDIN_METRICS.paid_follower_count_add
  ],
  [SOCIAL_NETWORK.TWITTER_SOCIAL]: [
    AVAILABLE_TWITTER_METRICS.retweet_count,
    AVAILABLE_TWITTER_METRICS.like_count,
    AVAILABLE_TWITTER_METRICS.followers_count
  ]
}

type TMetricByKeyValue = {
  [key in
    | AVAILABLE_FACEBOOK_METRICS
    | AVAILABLE_INSTAGRAM_METRICS
    | AVAILABLE_LINKEDIN_METRICS
    | AVAILABLE_TWITTER_METRICS]: TMetricValue[]
}

export type TMetricTypeByKeyValue = {
  [TYPE_GRAPHIC.TYPE_COMULATIVE]: {
    [key in TYPE_METRIC]?: TMetricValue[]
  }
  [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {
    [key in TYPE_METRIC]?: TMetricValue[]
  }
}

export type TBubbleData = {
  x: number
  y: number
  r: number
  time: number
}

export interface IAggregatedChartData {
  [TYPE_GRAPHIC.TYPE_COMULATIVE]: {
    labels?: string[] | number[]
    dataPositive?: string[] | number[] | TBubbleData[]
    dataNegative?: string[] | number[]
    data?: string[] | number[]
    average?: number
    paid?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels?: string[] | number[]
      average?: number
    }
    organic?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels: string[] | number[]
      average?: number
    }
    all?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels: string[] | number[]
      average?: number
    }
  }
  [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {
    labels?: string[] | number[]
    dataPositive?: string[] | number[] | { x: number; y: number; r: number }[]
    dataNegative?: string[] | number[]
    data?: string[] | number[]
    average?: number
    paid?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels: string[] | number[]
      average?: number
    }
    organic?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels: string[] | number[]
      average?: number
    }
    all?: {
      data?: string[] | number[]
      dataPositive?: string[] | number[]
      dataNegative?: string[] | number[]
      labels: string[] | number[]
      average?: number
    }
  }
}

export type TGraphicByKeyValue = {
  [key in AVAILABLE_GRAPHIC]?: IAggregatedChartData
}

export type TIntervalValue = {
  [key in TInterval]: TGraphicByKeyValue
}

export type TIntervalFollowersCard = {
  [key in TInterval]: TFollowersCard
}

export type TFollowersCard = {
  difference: string | number
  totalFollowers: number
}

type TIntervalName = {
  [key in TInterval]: string
}
export const INTERVAL_NAME: TIntervalName = {
  [TInterval.daily]: 'day',
  [TInterval.weekly]: 'week',
  [TInterval.monthly]: 'month',
  [TInterval.quarterly]: 'quarter'
}

export const INTERVAL_DURATION: TIntervalName = {
  [TInterval.daily]: format(new Date(), 'd MMM yyyy'),
  [TInterval.weekly]: `${format(startOfWeek(new Date()), 'd MMM yyyy')} TO ${format(
    endOfWeek(new Date()),
    'd MMM yyyy'
  )}`,
  [TInterval.monthly]: `${format(startOfMonth(new Date()), 'd MMM yyyy')} TO ${format(
    endOfMonth(new Date()),
    'd MMM yyyy'
  )}`,
  [TInterval.quarterly]: `${format(startOfQuarter(new Date()), 'd MMM yyyy')} TO ${format(
    endOfQuarter(new Date()),
    'd MMM yyyy'
  )}`
}

const MAX_DAYS_RANGE_METRICS_FACEBOOK = 730
const MAX_DAYS_RANGE_METRICS_INSTAGRAM = 730
const MAX_DAYS_RANGE_METRICS_LINKEDIN = 365
//twitter allows less than 3200 last posts

const maxDatepickerDate = new Date()
const minDatepickerDate = subDays(maxDatepickerDate, MAX_DAYS_RANGE_METRICS_LINKEDIN - 1)

interface AnalyticsProps {}

const Analytics: FC<AnalyticsProps> = () => {
  const dispatch = useDispatch()
  const { projectId, availableSocialNetworks } = useSelector(
    ({
      project: {
        currentProject: { id: projectId },
        availableSocialNetworks
      }
    }) => ({ availableSocialNetworks, projectId })
  )

  const [startEndDatesForDatePicker, setStartEndDatesForDatePicker] = useState<[Date, Date]>([
    subMonths(new Date(), 1),
    new Date()
  ])
  const [facebookData, setFacebookData] = useState<TIntervalValue | null>(null)
  const [facebookFollowersCardData, setFacebookFollowersCardData] =
    useState<TIntervalFollowersCard | null>(null)

  const [instagramData, setInstagramData] = useState<TIntervalValue | null>(null)
  const [instagramFollowersCardData, setInstagramFollowersCardData] =
    useState<TIntervalFollowersCard | null>(null)

  const [linkedinData, setLinkedinData] = useState<TIntervalValue | null>(null)
  const [linkedinFollowersCardData, setLinkedinFollowersCardData] =
    useState<TIntervalFollowersCard | null>(null)

  const [twitterData, setTwitterData] = useState<TIntervalValue | null>(null)
  const [twitterFollowersCardData, setTwitterFollowersCardData] =
    useState<TIntervalFollowersCard | null>(null)

  const [facebookFollowerInterval, setFacebookFollowerInterval] = useState<TInterval>(
    TInterval.daily
  )
  const [instagramFollowerInterval, setInstagramFollowerInterval] = useState<TInterval>(
    TInterval.daily
  )
  const [linkedFollowerInterval, setLinkedFollowerInterval] = useState<TInterval>(TInterval.daily)
  const [twitterFollowerInterval, setTwitterFollowerInterval] = useState<TInterval>(TInterval.daily)

  const [facebookTopPerformance, setFacebookTopPerformance] = useState<ITopPerformance[] | []>([])
  const [instagramTopPerformance, setInstagramTopPerformance] = useState<ITopPerformance[] | []>([])
  const [linkedinTopPerformance, setLinkedinTopPerformance] = useState<ITopPerformance[] | []>([])
  const [twitterTopPerformance, setTwitterTopPerformance] = useState<ITopPerformance[] | []>([])

  const [fbPerformanceType, setFbPerformanceType] = useState<TTopPerformance>(TTopPerformance.reach)
  const [instaPerformanceType, setInstaPerformanceType] = useState<TTopPerformance>(
    TTopPerformance.reach
  )
  const [linkedinPerformanceType, setLinkedinPerformanceType] = useState<TTopPerformance>(
    TTopPerformance.reach
  )
  const [twitterPerformanceType, setTwitterPerformanceType] = useState<TTopPerformance>(
    TTopPerformance.reach
  )

  const onChangeDates = (dates: [Date, Date]) => {
    if (isSameDay(dates[0], dates[1])) {
      const nextDayAfterCurrent = addDays(dates[0], 1)
      setStartEndDatesForDatePicker([dates[0], nextDayAfterCurrent])
    } else {
      setStartEndDatesForDatePicker([...dates])
    }
  }

  const { social: activeSocial } = useParams<{ social: SOCIAL_NETWORK }>()
  const setActiveSocial = (social: SOCIAL_NETWORK) => {
    dispatch(push(generatePath(AUTH_ROUTE.ANALYTICS, { social, projectId })))
  }

  useEffect(() => {
    dispatch(setHeaderView(HEADER_VIEW.ANALYTICS))
  }, [])

  useEffect(() => {
    if (
      !availableSocialNetworks.find((social) => social === activeSocial) &&
      availableSocialNetworks.length
    ) {
      setActiveSocial(availableSocialNetworks[0])
    }
  }, [activeSocial, availableSocialNetworks, setActiveSocial])

  const groupData = (data: any, interval: string) => {
    if (interval === 'daily') {
      return data.reduce((acc: TMetricByKeyValue, value: TMetricValue) => {
        const { name } = value
        acc[name] ? acc[name].push(value) : (acc[name] = [value])
        return acc
      }, {})
    } else {
      let groupedMetrics: any = data.reduce((acc: any, value: TMetricValue) => {
        const { name, date } = value
        let dateInterval: string = ''
        if (interval === 'weekly') {
          dateInterval = format(startOfWeek(parse(date, 'yyyy-MM-dd', new Date())), 'yyyy-MM-dd')
        } else if (interval === 'monthly') {
          dateInterval = format(startOfMonth(parse(date, 'yyyy-MM-dd', new Date())), 'yyyy-MM-dd')
        } else if (interval === 'quarterly') {
          dateInterval = format(startOfQuarter(parse(date, 'yyyy-MM-dd', new Date())), 'yyyy-MM-dd')
        }
        if (!acc[name]) {
          acc[name] = []
        }
        if (!acc[name][dateInterval]) {
          acc[name][dateInterval] = []
        }
        acc[name][dateInterval].push(value)
        return acc
      }, {})

      let groupedData: any = {}
      Object.keys(groupedMetrics).forEach((v) => {
        Object.keys(groupedMetrics[v]).forEach((d) => {
          const sum = groupedMetrics[v][d].reduce((t: any, s: any) => t + s.value, 0)
          const extended_value = groupedMetrics[v][d].reduce(
            (t: any, s: any) => t + s.extended_value!,
            0
          )
          if (!groupedData[v]) {
            groupedData[v] = []
          }
          groupedData[v].push({ date: d, extended_value: extended_value, name: v, value: sum })
        }, {})
      })
      return groupedData
    }
  }

  useEffect(() => {
    const getInsights = async () => {
      setFacebookData(null)
      const [from, to] = startEndDatesForDatePicker

      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.FACEBOOK_SOCIAL,
        metrics: metricsForRequest[SOCIAL_NETWORK.FACEBOOK_SOCIAL].join(','),
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd')
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const fbDataAgg = Object.values(INTERVAL).reduce((intervalAcc: any, interval: any) => {
          const intervalData = groupData(data, interval)

          const aggData = Object.values(SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL]).reduce(
            (acc: TGraphicByKeyValue, currentValue: TGraphic | undefined) => {
              const { name, metrics, settings } = currentValue!
              const metricsDataByGraphicName = metrics.reduce(
                (acc2: TMetricTypeByKeyValue, { type, metric }: TMetric) => {
                  if (intervalData[metric]) {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = !intervalData[metric]
                      ? []
                      : intervalData[metric]

                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = !intervalData[metric]
                      ? []
                      : intervalData[metric].reduce(
                          (acc3: TMetricValue[], metricValue: any, currentIndex: any) => {
                            if (currentIndex === 0) {
                              acc3.push(metricValue)
                            } else {
                              const { value, extended_value } = acc3[currentIndex - 1]
                              if (value !== null) {
                                acc3.push({ ...metricValue, value: value + metricValue.value! })
                              } else {
                                acc3.push({
                                  ...metricValue,
                                  extended_value: extended_value! + metricValue.extended_value!
                                })
                              }
                            }
                            return acc3
                          },
                          []
                        )
                    return acc2
                  } else {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = []
                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = []
                    return acc2
                  }
                },
                { [TYPE_GRAPHIC.TYPE_COMULATIVE]: {}, [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {} }
              )

              const chartData = preperDataForCharts(name, settings, metricsDataByGraphicName)
              acc[name] = chartData
              return acc
            },
            {}
          )
          intervalAcc[interval] = aggData
          return intervalAcc
        }, {})

        setFacebookData(fbDataAgg)
      } else {
        console.error(response)
      }
    }
    if (
      availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.FACEBOOK_SOCIAL) &&
      startEndDatesForDatePicker[0] &&
      startEndDatesForDatePicker[1]
    )
      getInsights()
  }, [startEndDatesForDatePicker, projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setFacebookFollowersCardData(null)
      const today = new Date()
      const yesterday = subDays(today, 1)
      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.FACEBOOK_SOCIAL,
        metrics: AVAILABLE_FACEBOOK_METRICS.page_fans,
        from: format(yesterday, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
        interval: 'all-last-two'
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const totalFollowers = Object.keys(data).reduce((acc: any, key: string) => {
          if (!acc[key]) {
            acc[key] = {}
          }

          const todayValue = data[key].page_fans[0] ? data[key].page_fans[0] : null
          const yesterdayValue = data[key].page_fans[1] ? data[key].page_fans[1] : null
          if (todayValue && yesterdayValue) {
            const { value: lastValue, extended_value: lastExtendedValue } = todayValue
            const { value: preLastValue, extended_value: preLastExtendedValue } = yesterdayValue

            if (lastValue !== null) {
              let difference = (
                ((lastValue - preLastValue!) / (preLastValue! === 0 ? 1 : preLastValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastValue
              }
              acc[key] = totalFollowersData
            } else {
              let difference = (
                ((lastExtendedValue! - preLastExtendedValue!) /
                  (preLastExtendedValue! === 0 ? 1 : preLastExtendedValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastExtendedValue!
              }
              acc[key] = totalFollowersData
            }
          } else {
            const totalFollowersData: TFollowersCard = {
              difference: 0,
              totalFollowers: todayValue ? todayValue.value : 0
            }
            acc[key] = totalFollowersData
          }
          return acc
        }, {})
        setFacebookFollowersCardData(totalFollowers)
      } else {
        console.error(response)
      }
    }
    if (availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.FACEBOOK_SOCIAL))
      getInsights()
  }, [projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getTopPerformance = async () => {
      const response = await dispatch(
        getTopPerformanceThunk({
          projectId,
          social: SOCIAL_NETWORK.FACEBOOK_SOCIAL,
          type: fbPerformanceType
        })
      )
      if (isFulfilled(response)) {
        const { payload } = response
        setFacebookTopPerformance(payload)
      }
    }

    getTopPerformance()
  }, [projectId, availableSocialNetworks, fbPerformanceType, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setInstagramData(null)
      const [from, to] = startEndDatesForDatePicker

      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.INSTAGRAM_SOCIAL,
        metrics: metricsForRequest[SOCIAL_NETWORK.INSTAGRAM_SOCIAL].join(','),
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd')
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response

        const instDataAgg = Object.values(INTERVAL).reduce((intervalAcc: any, interval: any) => {
          const intervalData = groupData(data, interval)
          const aggData = Object.values(SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL]).reduce(
            (acc: TGraphicByKeyValue, currentValue: TGraphic | undefined) => {
              const { name, metrics, settings } = currentValue!
              const metricsDataByGraphicName = metrics.reduce(
                (acc2: TMetricTypeByKeyValue, { type, metric }: TMetric) => {
                  if (intervalData[metric]) {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = !intervalData[metric]
                      ? []
                      : intervalData[metric]
                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = !intervalData[metric]
                      ? []
                      : intervalData[metric].reduce(
                          (acc3: TMetricValue[], metricValue: any, currentIndex: any) => {
                            if (currentIndex === 0) {
                              acc3.push(metricValue)
                            } else {
                              const { value, extended_value } = acc3[currentIndex - 1]
                              if (value !== null) {
                                acc3.push({ ...metricValue, value: value + metricValue.value! })
                              } else {
                                acc3.push({
                                  ...metricValue,
                                  extended_value: extended_value! + metricValue.extended_value!
                                })
                              }
                            }
                            return acc3
                          },
                          []
                        )
                    return acc2
                  } else {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = []
                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = []
                    return acc2
                  }
                },
                { [TYPE_GRAPHIC.TYPE_COMULATIVE]: {}, [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {} }
              )
              const chartData = preperDataForCharts(name, settings, metricsDataByGraphicName)
              acc[name] = chartData
              return acc
            },
            {}
          )
          intervalAcc[interval] = aggData
          return intervalAcc
        }, {})

        setInstagramData(instDataAgg)
      } else {
        console.error(response)
      }
    }
    if (
      availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.INSTAGRAM_SOCIAL) &&
      startEndDatesForDatePicker[0] &&
      startEndDatesForDatePicker[1]
    )
      getInsights()
  }, [startEndDatesForDatePicker, projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setInstagramFollowersCardData(null)
      const today = new Date()
      const yesterday = subDays(today, 1)
      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.INSTAGRAM_SOCIAL,
        metrics: AVAILABLE_INSTAGRAM_METRICS.follower_count,
        from: format(yesterday, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
        interval: 'all-last-two'
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const totalFollowers = Object.keys(data).reduce((acc: any, key: string) => {
          if (!acc[key]) {
            acc[key] = {}
          }
          const todayValue = data[key].follower_count[0] ? data[key].follower_count[0] : null
          const yesterdayValue = data[key].follower_count[1] ? data[key].follower_count[1] : null

          if (todayValue && yesterdayValue) {
            const { value: lastValue, extended_value: lastExtendedValue } = todayValue
            const { value: preLastValue, extended_value: preLastExtendedValue } = yesterdayValue

            if (lastValue !== null) {
              let difference = (
                ((lastValue - preLastValue!) / (preLastValue! === 0 ? 1 : preLastValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastValue
              }
              acc[key] = totalFollowersData
            } else {
              let difference = (
                ((lastExtendedValue! - preLastExtendedValue!) /
                  (preLastExtendedValue! === 0 ? 1 : preLastExtendedValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastExtendedValue!
              }
              acc[key] = totalFollowersData
            }
          } else {
            const totalFollowersData: TFollowersCard = {
              difference: 0,
              totalFollowers: todayValue ? todayValue.value : 0
            }
            acc[key] = totalFollowersData
          }
          return acc
        }, {})
        setInstagramFollowersCardData(totalFollowers)
      } else {
        console.error(response)
      }
    }
    if (availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.INSTAGRAM_SOCIAL))
      getInsights()
  }, [projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getTopPerformance = async () => {
      const response = await dispatch(
        getTopPerformanceThunk({
          projectId,
          social: SOCIAL_NETWORK.INSTAGRAM_SOCIAL,
          type: instaPerformanceType
        })
      )
      if (isFulfilled(response)) {
        const { payload } = response
        setInstagramTopPerformance(payload)
      }
    }

    getTopPerformance()
  }, [projectId, availableSocialNetworks, instaPerformanceType, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setLinkedinData(null)
      const [from, to] = startEndDatesForDatePicker

      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.LINKEDIN_SOCIAL,
        metrics: metricsForRequest[SOCIAL_NETWORK.LINKEDIN_SOCIAL].join(','),
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd')
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response

        //Merge several metrics to one
        const linkedinDataAgg = Object.values(INTERVAL).reduce(
          (intervalAcc: any, interval: any) => {
            const intervalData = groupData(data, interval)
            intervalData[AVAILABLE_LINKEDIN_METRICS.total_engagement] = totalEngagement
              .reduce((acc: any, currentKey) => {
                intervalData &&
                  intervalData[currentKey] &&
                  intervalData[currentKey].forEach((element: any, index: any) => {
                    acc[index] ? acc[index].push(element) : (acc[index] = [element])
                  })
                return acc
              }, [])
              .map((element: TMetricValue[]) => {
                //what should we use? extended_value or valuy? if value === null we use extended_value
                if (element[0].value !== null) {
                  const totalCount = element.reduce(
                    (count: number, { value }: TMetricValue) => (count += value!),
                    0
                  )
                  const { extended_value, date } = element[0]
                  const newElementOfTotalEngagement = {
                    extended_value,
                    date,
                    value: totalCount,
                    name: AVAILABLE_LINKEDIN_METRICS.total_engagement
                  }
                  return newElementOfTotalEngagement
                } else {
                  const totalCount = element.reduce(
                    (count: number, { extended_value }: TMetricValue) => (count += extended_value!),
                    0
                  )
                  const { date, value } = element[0]
                  const newElementOfTotalEngagement = {
                    extended_value: totalCount,
                    date,
                    value,
                    name: AVAILABLE_LINKEDIN_METRICS.total_engagement
                  }
                  return newElementOfTotalEngagement
                }
              })
            const aggData = Object.values(SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL]).reduce(
              (acc: TGraphicByKeyValue, currentValue: TGraphic | undefined) => {
                const { name, metrics, settings } = currentValue!
                const metricsDataByGraphicName = metrics.reduce(
                  (acc2: TMetricTypeByKeyValue, { type, metric }: TMetric) => {
                    if (intervalData[metric]) {
                      acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = intervalData[metric]
                      acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = intervalData[metric].reduce(
                        (acc3: TMetricValue[], metricValue: any, currentIndex: any) => {
                          if (currentIndex === 0) {
                            acc3.push(metricValue)
                          } else {
                            const { value, extended_value } = acc3[currentIndex - 1]
                            if (value !== null) {
                              acc3.push({ ...metricValue, value: value + metricValue.value! })
                            } else {
                              acc3.push({
                                ...metricValue,
                                extended_value: extended_value! + metricValue.extended_value!
                              })
                            }
                          }
                          return acc3
                        },
                        []
                      )
                      return acc2
                    } else {
                      acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = []
                      acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = []
                      return acc2
                    }
                  },
                  { [TYPE_GRAPHIC.TYPE_COMULATIVE]: {}, [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {} }
                )
                const chartData = preperDataForCharts(name, settings, metricsDataByGraphicName)
                acc[name] = chartData
                return acc
              },
              {}
            )
            intervalAcc[interval] = aggData
            return intervalAcc
          },
          {}
        )
        setLinkedinData(linkedinDataAgg)
      } else {
        console.error(response)
      }
    }
    if (
      availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.LINKEDIN_SOCIAL) &&
      startEndDatesForDatePicker[0] &&
      startEndDatesForDatePicker[1]
    )
      getInsights()
  }, [startEndDatesForDatePicker, projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setLinkedinFollowersCardData(null)
      const today = new Date()
      const yesterday = subDays(today, 1)
      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.LINKEDIN_SOCIAL,
        metrics: [
          AVAILABLE_LINKEDIN_METRICS.paid_follower_count,
          AVAILABLE_LINKEDIN_METRICS.organic_follower_count
        ].join(','),
        from: format(yesterday, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
        interval: 'all-last-two'
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const totalFollowers = Object.keys(data).reduce((acc: any, key: string) => {
          if (!acc[key]) {
            acc[key] = {}
          }
          const todayPaidFollowerCount = data[key].paidFollowerCount[0]
            ? data[key].paidFollowerCount[0]
            : {}
          const yesterdayPaidFollowerCount = data[key].paidFollowerCount[1]
            ? data[key].paidFollowerCount[1]
            : {}
          const todayOrganicFollowerCount = data[key].organicFollowerCount[0]
            ? data[key].organicFollowerCount[0]
            : {}
          const yesterdayOrganicFollowerCount = data[key].organicFollowerCount[1]
            ? data[key].organicFollowerCount[1]
            : {}
          let todayValue
          let yesterdayValue
          if (todayPaidFollowerCount || todayOrganicFollowerCount) {
            todayValue = {
              ...todayPaidFollowerCount,
              ...todayOrganicFollowerCount,
              value:
                (todayPaidFollowerCount.value ? todayPaidFollowerCount.value : 0) +
                (todayOrganicFollowerCount.value ? todayOrganicFollowerCount.value : 0)
            }
          }

          if (yesterdayOrganicFollowerCount || yesterdayPaidFollowerCount) {
            yesterdayValue = {
              ...yesterdayPaidFollowerCount,
              ...yesterdayOrganicFollowerCount,
              value:
                (yesterdayPaidFollowerCount.value ? yesterdayPaidFollowerCount.value : 0) +
                (yesterdayOrganicFollowerCount.value ? yesterdayOrganicFollowerCount.value : 0)
            }
          }

          if (todayValue && yesterdayValue) {
            const { value: lastValue, extended_value: lastExtendedValue } = todayValue
            const { value: preLastValue, extended_value: preLastExtendedValue } = yesterdayValue

            if (lastValue !== null) {
              let difference = (
                ((lastValue - preLastValue!) / (preLastValue! === 0 ? 1 : preLastValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastValue
              }
              acc[key] = totalFollowersData
            } else {
              let difference = (
                ((lastExtendedValue! - preLastExtendedValue!) /
                  (preLastExtendedValue! === 0 ? 1 : preLastExtendedValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastExtendedValue!
              }
              acc[key] = totalFollowersData
            }
          } else {
            const totalFollowersData: TFollowersCard = {
              difference: 0,
              totalFollowers: todayValue ? todayValue.value : 0
            }
            acc[key] = totalFollowersData
          }
          return acc
        }, {})
        setLinkedinFollowersCardData(totalFollowers)
      } else {
        console.error(response)
      }
    }
    if (availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.LINKEDIN_SOCIAL))
      getInsights()
  }, [projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getTopPerformance = async () => {
      const response = await dispatch(
        getTopPerformanceThunk({
          projectId,
          social: SOCIAL_NETWORK.LINKEDIN_SOCIAL,
          type: linkedinPerformanceType
        })
      )
      if (isFulfilled(response)) {
        const { payload } = response
        setLinkedinTopPerformance(payload)
      }
    }

    getTopPerformance()
  }, [projectId, availableSocialNetworks, linkedinPerformanceType, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setTwitterData(null)
      const [from, to] = startEndDatesForDatePicker

      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.TWITTER_SOCIAL,
        metrics: metricsForRequest[SOCIAL_NETWORK.TWITTER_SOCIAL].join(','),
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd')
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const metricsByKeyValue: TMetricByKeyValue = data.reduce(
          (acc: TMetricByKeyValue, value: TMetricValue) => {
            const { name } = value
            acc[name] ? acc[name].push(value) : (acc[name] = [value])
            return acc
          },
          {}
        )

        const twitterDataAgg = Object.values(INTERVAL).reduce((intervalAcc: any, interval: any) => {
          const intervalData = groupData(data, interval)
          const aggData = Object.values(SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL]).reduce(
            (acc: TGraphicByKeyValue, currentValue: TGraphic | undefined) => {
              const { name, metrics, settings } = currentValue!
              const metricsDataByGraphicName = metrics.reduce(
                (acc2: TMetricTypeByKeyValue, { type, metric }: TMetric) => {
                  if (intervalData[metric]) {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = intervalData[metric]
                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = intervalData[metric].reduce(
                      (acc3: TMetricValue[], metricValue: any, currentIndex: any) => {
                        if (currentIndex === 0) {
                          acc3.push(metricValue)
                        } else {
                          const { value, extended_value } = acc3[currentIndex - 1]
                          if (value !== null) {
                            acc3.push({ ...metricValue, value: value + metricValue.value! })
                          } else {
                            acc3.push({
                              ...metricValue,
                              extended_value: extended_value! + metricValue.extended_value!
                            })
                          }
                        }
                        return acc3
                      },
                      []
                    )
                    return acc2
                  } else {
                    acc2[TYPE_GRAPHIC.TYPE_NON_COMULATIVE][type] = []
                    acc2[TYPE_GRAPHIC.TYPE_COMULATIVE][type] = []
                    return acc2
                  }
                },
                { [TYPE_GRAPHIC.TYPE_COMULATIVE]: {}, [TYPE_GRAPHIC.TYPE_NON_COMULATIVE]: {} }
              )
              const chartData = preperDataForCharts(name, settings, metricsDataByGraphicName)
              acc[name] = chartData
              return acc
            },
            {}
          )
          intervalAcc[interval] = aggData
          return intervalAcc
        }, {})

        setTwitterData(twitterDataAgg)
      } else {
        console.error(response)
      }
    }
    if (
      availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.TWITTER_SOCIAL) &&
      startEndDatesForDatePicker[0] &&
      startEndDatesForDatePicker[1]
    )
      getInsights()
  }, [startEndDatesForDatePicker, projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getInsights = async () => {
      setTwitterFollowersCardData(null)
      const today = new Date()
      const yesterday = subDays(today, 1)
      const payload: TGetDailyInsightsBySocialNetworkAndMetricPayload = {
        project_id: projectId,
        social_network: SOCIAL_NETWORK.TWITTER_SOCIAL,
        metrics: AVAILABLE_TWITTER_METRICS.followers_count,
        from: format(yesterday, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
        interval: 'all-last-two'
      }

      const response = await dispatch(getDailyInsightsBySocialNetworkAndMetricThunk(payload))
      if (isFulfilled(response)) {
        const {
          payload: { data }
        } = response
        const totalFollowers = Object.keys(data).reduce((acc: any, key: string) => {
          if (!acc[key]) {
            acc[key] = {}
          }
          const todayValue = data[key].followers_count[0] ? data[key].followers_count[0] : null
          const yesterdayValue = data[key].followers_count[1] ? data[key].followers_count[1] : null

          if (todayValue && yesterdayValue) {
            const { value: lastValue, extended_value: lastExtendedValue } = todayValue
            const { value: preLastValue, extended_value: preLastExtendedValue } = yesterdayValue

            if (lastValue !== null) {
              let difference = (
                ((lastValue - preLastValue!) / (preLastValue! === 0 ? 1 : preLastValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastValue
              }
              acc[key] = totalFollowersData
            } else {
              let difference = (
                ((lastExtendedValue! - preLastExtendedValue!) /
                  (preLastExtendedValue! === 0 ? 1 : preLastExtendedValue!)) *
                100
              ).toFixed(2)
              difference = Number(difference) > 100 ? '100' : difference
              const totalFollowersData: TFollowersCard = {
                difference: difference ? difference : 0,
                totalFollowers: lastExtendedValue!
              }
              acc[key] = totalFollowersData
            }
          } else {
            const totalFollowersData: TFollowersCard = {
              difference: 0,
              totalFollowers: todayValue ? todayValue.value : 0
            }
            acc[key] = totalFollowersData
          }
          return acc
        }, {})
        setTwitterFollowersCardData(totalFollowers)
      } else {
        console.error(response)
      }
    }
    if (availableSocialNetworks.find((social) => social === SOCIAL_NETWORK.TWITTER_SOCIAL))
      getInsights()
  }, [projectId, availableSocialNetworks, dispatch])

  useEffect(() => {
    const getTopPerformance = async () => {
      const response = await dispatch(
        getTopPerformanceThunk({
          projectId,
          social: SOCIAL_NETWORK.TWITTER_SOCIAL,
          type: twitterPerformanceType
        })
      )
      if (isFulfilled(response)) {
        const { payload } = response
        setTwitterTopPerformance(payload)
      }
    }

    getTopPerformance()
  }, [projectId, availableSocialNetworks, twitterPerformanceType, dispatch])

  const renderTimezoneFBNote = () => (
    <div className='analytics-social-graphs__content__note'>
      {/* <QuestionIcon /> */}
      Note: All metrics retrieved are based on Pacific Daylight Time (GMT-7)
    </div>
  )

  return (
    <>
      <MainHeader />
      {availableSocialNetworks.length ? (
        <div className='analytics-wrapper'>
          <div className='analytics-wrapper__datepicker'>
            <DateRangePicker
              dates={startEndDatesForDatePicker}
              datePickerProps={{
                onChange: onChangeDates,
                maxDate: maxDatepickerDate,
                minDate: minDatepickerDate
              }}
            />
          </div>
          <div className='analytics-social-graphs'>
            <div className='analytics-social-graphs__header'>
              {' '}
              <AnalysticsSocialToggler
                socials={availableSocialNetworks}
                activeSocial={activeSocial}
                setActiveSocial={setActiveSocial}
              />
              {renderTimezoneFBNote()}
            </div>
            <div className='analytics-social-graphs__content'>
              {/* Facebook */}
              {activeSocial === SOCIAL_NETWORK.FACEBOOK_SOCIAL && (
                <>
                  {facebookData && facebookFollowersCardData ? (
                    <>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='d-flex flex-row p-relative'>
                          <div className='header-absolute graphic-wrapper__header__name'>
                            TOTAL FOLLOWERS
                          </div>
                          <div className='graphic-wrapper__graphic graphic-wrapper__graphic-card-wrapper'>
                            <div
                              className={`graphic-wrapper__card ${
                                facebookFollowersCardData[facebookFollowerInterval]!.difference < 0
                                  ? 'graphic-wrapper__card__negative'
                                  : 'graphic-wrapper__card__positive'
                              }`}
                            >
                              {/* <div className='graphic-wrapper__card-title'>Total fans</div> */}
                              <div className='graphic-wrapper__card-value'>
                                {
                                  facebookFollowersCardData[facebookFollowerInterval]!
                                    .totalFollowers
                                }
                              </div>
                              <div className='graphic-wrapper__card-sub-value'>
                                {INTERVAL_DURATION[facebookFollowerInterval]}
                              </div>
                              {facebookFollowersCardData[facebookFollowerInterval]!.difference <
                              0 ? (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-down'>
                                  {facebookFollowersCardData[facebookFollowerInterval]!.difference}%
                                  vs last {INTERVAL_NAME[facebookFollowerInterval]}
                                </div>
                              ) : (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-up'>
                                  +{facebookFollowersCardData[facebookFollowerInterval]!.difference}
                                  % vs last {INTERVAL_NAME[facebookFollowerInterval]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='graphic-wrapper__graphic w-without-card'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                            data={facebookData}
                            setFollowerInterval={setFacebookFollowerInterval}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                            data={facebookData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                            data={facebookData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                            data={facebookData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.FACEBOOK_SOCIAL][
                                AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                            data={facebookData}
                          />
                        </div>
                      </div>
                      <TopPerformance
                        social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                        data={facebookTopPerformance}
                        type={fbPerformanceType}
                        setType={setFbPerformanceType}
                      />
                    </>
                  ) : (
                    <Preloader />
                  )}
                </>
              )}
              {/* Facebook */}

              {/* Instagram */}
              {activeSocial === SOCIAL_NETWORK.INSTAGRAM_SOCIAL && (
                <>
                  {instagramData && instagramFollowersCardData ? (
                    <>
                      {/* {renderTimezoneFBNote()} */}
                      <div className='analytics-social-graphs__content__item'>
                        <div className='d-flex flex-row p-relative'>
                          <div className='header-absolute graphic-wrapper__header__name'>
                            TOTAL FOLLOWERS
                          </div>
                          <div className='graphic-wrapper__graphic'>
                            <div
                              className={`graphic-wrapper__card ${
                                instagramFollowersCardData[instagramFollowerInterval]!.difference <
                                0
                                  ? 'graphic-wrapper__card__negative'
                                  : 'graphic-wrapper__card__positive'
                              }`}
                            >
                              <div className='graphic-wrapper__card-value'>
                                {
                                  instagramFollowersCardData[instagramFollowerInterval]!
                                    .totalFollowers
                                }
                              </div>
                              <div className='graphic-wrapper__card-sub-value'>
                                {INTERVAL_DURATION[instagramFollowerInterval]}
                              </div>
                              {instagramFollowersCardData[instagramFollowerInterval]!.difference <
                              0 ? (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-down'>
                                  {
                                    instagramFollowersCardData[instagramFollowerInterval]!
                                      .difference
                                  }
                                  % vs last {INTERVAL_NAME[instagramFollowerInterval]}
                                </div>
                              ) : (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-up'>
                                  +
                                  {
                                    instagramFollowersCardData[instagramFollowerInterval]!
                                      .difference
                                  }
                                  % vs last {INTERVAL_NAME[instagramFollowerInterval]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='graphic-wrapper__graphic w-without-card'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.NEW_FOLLOWERS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.NEW_FOLLOWERS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.NEW_FOLLOWERS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                            setFollowerInterval={setInstagramFollowerInterval}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_REACH
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_REACH
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_REACH
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_REACH
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.INSTAGRAM_SOCIAL][
                                AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                            data={instagramData}
                          />
                        </div>
                      </div>
                      <TopPerformance
                        social={SOCIAL_NETWORK.INSTAGRAM_SOCIAL}
                        data={instagramTopPerformance}
                        type={instaPerformanceType}
                        setType={setInstaPerformanceType}
                      />
                    </>
                  ) : (
                    <Preloader />
                  )}
                </>
              )}
              {/* Instagram */}

              {/* Linkedin */}
              {activeSocial === SOCIAL_NETWORK.LINKEDIN_SOCIAL && (
                <>
                  {linkedinData && linkedinFollowersCardData ? (
                    <>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic graphic-wrapper__graphic-card-wrapper'>
                          <div className='d-flex flex-row p-relative'>
                            <div className='header-absolute graphic-wrapper__header__name'>
                              <span>TOTAL FOLLOWERS</span>
                              <div className='ml-5 custom-tooltip'>
                                <QuestionIcon />
                                <div className='custom-tooltip-block'>
                                  Any data from the last 48 hours is not displayed due to API
                                  restrictions.
                                </div>
                              </div>
                            </div>
                            <div
                              className={`graphic-wrapper__card ${
                                linkedinFollowersCardData[linkedFollowerInterval]!.difference < 0
                                  ? 'graphic-wrapper__card__negative'
                                  : 'graphic-wrapper__card__positive'
                              }`}
                            >
                              {/* <div className='graphic-wrapper__card-title'>Total fans</div> */}
                              <div className='graphic-wrapper__card-value'>
                                {linkedinFollowersCardData[linkedFollowerInterval]!.totalFollowers}
                              </div>
                              <div className='graphic-wrapper__card-sub-value'>
                                {INTERVAL_DURATION[linkedFollowerInterval]}
                              </div>
                              {linkedinFollowersCardData[linkedFollowerInterval]!.difference < 0 ? (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-down'>
                                  {linkedinFollowersCardData[linkedFollowerInterval]!.difference}%
                                  vs last {INTERVAL_NAME[linkedFollowerInterval]}
                                </div>
                              ) : (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-up'>
                                  +{linkedinFollowersCardData[linkedFollowerInterval]!.difference}%
                                  vs last {INTERVAL_NAME[linkedFollowerInterval]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='graphic-wrapper__graphic w-without-card'>
                          <Graphic
                            tooltip='Any data from the last 48 hours is not displayed due to API restrictions.'
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                            data={linkedinData}
                            setFollowerInterval={setLinkedFollowerInterval}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            tooltip='Any data from the last 48 hours is not displayed due to API restrictions.'
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                            data={linkedinData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic w-48 divider'>
                          <Graphic
                           tooltip='Any data from the last 48 hours is not displayed due to API restrictions.'
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                            data={linkedinData}
                          />
                        </div>
                        <div className='graphic-wrapper__graphic w-48 divider'>
                          <Graphic
                            tooltip='Any data from the last 48 hours is not displayed due to API restrictions.'
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.ENGAGEMENT_RATE
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.ENGAGEMENT_RATE
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.ENGAGEMENT_RATE
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                            data={linkedinData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            tooltip='Any data from the last 48 hours is not displayed due to API restrictions.'
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.LINKEDIN_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                            data={linkedinData}
                          />
                        </div>
                      </div>
                      <TopPerformance
                        social={SOCIAL_NETWORK.LINKEDIN_SOCIAL}
                        data={linkedinTopPerformance}
                        type={linkedinPerformanceType}
                        setType={setLinkedinPerformanceType}
                      />
                    </>
                  ) : (
                    <Preloader />
                  )}
                </>
              )}
              {/* Linkedin */}

              {/* Twitter */}
              {activeSocial === SOCIAL_NETWORK.TWITTER_SOCIAL && (
                <>
                  {twitterData && twitterFollowersCardData ? (
                    <>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <div className='d-flex flex-row p-relative'>
                            <div className='header-absolute graphic-wrapper__header__name'>
                              TOTAL FOLLOWERS
                            </div>
                            <div
                              className={`graphic-wrapper__card ${
                                twitterFollowersCardData[twitterFollowerInterval]!.difference < 0
                                  ? 'graphic-wrapper__card__negative'
                                  : 'graphic-wrapper__card__positive'
                              }`}
                            >
                              {/* <div className='graphic-wrapper__card-title'>Total followers</div> */}
                              <div className='graphic-wrapper__card-value'>
                                {twitterFollowersCardData[twitterFollowerInterval]!.totalFollowers}
                              </div>
                              <div className='graphic-wrapper__card-sub-value'>
                                {INTERVAL_DURATION[twitterFollowerInterval]}
                              </div>
                              {twitterFollowersCardData[twitterFollowerInterval]!.difference < 0 ? (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-down'>
                                  {twitterFollowersCardData[twitterFollowerInterval]!.difference}%
                                  vs last {INTERVAL_NAME[twitterFollowerInterval]}
                                </div>
                              ) : (
                                <div className='graphic-wrapper__card-rate graphic-wrapper__card-rate-up'>
                                  +{twitterFollowersCardData[twitterFollowerInterval]!.difference}%
                                  vs last {INTERVAL_NAME[twitterFollowerInterval]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='graphic-wrapper__graphic w-without-card'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.TWITTER_SOCIAL}
                            data={twitterData}
                            setFollowerInterval={setTwitterFollowerInterval}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TWEET_LIKES
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TWEET_LIKES
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.TWEET_LIKES
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.TWITTER_SOCIAL}
                            data={twitterData}
                          />
                        </div>
                      </div>
                      <div className='analytics-social-graphs__content__item'>
                        <div className='graphic-wrapper__graphic'>
                          <Graphic
                            name={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.RETWEET
                              ]!.name
                            }
                            settings={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.RETWEET
                              ]!.settings
                            }
                            chart={
                              SOCIAL_GRAPHICS[SOCIAL_NETWORK.TWITTER_SOCIAL][
                                AVAILABLE_GRAPHIC.RETWEET
                              ]!.chartComponent
                            }
                            social={SOCIAL_NETWORK.TWITTER_SOCIAL}
                            data={twitterData}
                          />
                        </div>
                      </div>
                      <TopPerformance
                        social={SOCIAL_NETWORK.TWITTER_SOCIAL}
                        data={twitterTopPerformance}
                        type={twitterPerformanceType}
                        setType={setTwitterPerformanceType}
                      />
                    </>
                  ) : (
                    <Preloader />
                  )}
                </>
              )}
              {/* Twitter */}
            </div>
          </div>
        </div>
      ) : (
        <div className='analytics-wrapper analytics-wrapper__empty-background'>
          <img className='analytics-wrapper__empty-background-img' src={EmptyBackgorund} alt='' />
          <div className='analytics-wrapper__empty-background-title'>
            You don't have any connected social networks
          </div>
        </div>
      )}
    </>
  )
}

export default Analytics
function dispatch(arg0: {
  payload: import('../../store/application/types').HEADER_VIEW
  type: string
}) {
  throw new Error('Function not implemented.')
}
